import React, { useState, useEffect, useCallback } from 'react';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { setSelectedRestaurantId } from '../../redux/slices/restaurantSlice';
import { Dropdown } from '../common/Dropdown';

interface NumberAssignTestModalProps {
  isOpen: boolean;
  onClose: () => void;
  endpoint: {
    title: string;
    method: string;
    endpoint: string;
    parameters?: any;
  };
}

export const NumberAssignTestModal: React.FC<NumberAssignTestModalProps> = ({ 
  isOpen, 
  onClose, 
  endpoint 
}) => {
  const dispatch = useAppDispatch();
  const { apiBaseUrl, currentApiKey, environment } = useAppSelector((state) => state.auth);
  const { restaurants, selectedRestaurantId: reduxSelectedRestaurantId } = useAppSelector((state) => state.restaurant);
  
  // Auth state
  const [restaurantId, setRestaurantId] = useState('');
  const [selectedRestaurantName, setSelectedRestaurantName] = useState('');
  
  // Form state
  const [phoneNumber, setPhoneNumber] = useState('');
  const [availableNumbers, setAvailableNumbers] = useState<string[]>([]);
  const [loadingNumbers, setLoadingNumbers] = useState(false);
  const [assignedNumber, setAssignedNumber] = useState('');
  const [isNumberAssigned, setIsNumberAssigned] = useState(false);
  
  // Response state
  const [response, setResponse] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [authExpanded, setAuthExpanded] = useState(true);

  // Initialize restaurant selection from Redux
  useEffect(() => {
    if (isOpen && reduxSelectedRestaurantId && restaurants.length > 0) {
      const restaurant = restaurants.find(r => r.restaurantId === reduxSelectedRestaurantId);
      if (restaurant) {
        setRestaurantId(restaurant.restaurantId);
        setSelectedRestaurantName(restaurant.name);
      }
    }
  }, [isOpen, reduxSelectedRestaurantId, restaurants]);

  // Memoized fetch function with proper dependencies
  const fetchAvailableNumbers = useCallback(async () => {
    if (!restaurantId || !currentApiKey) {
      return;
    }
    
    setLoadingNumbers(true);
    setAvailableNumbers([]);
    setPhoneNumber(''); // Clear selected number when fetching new list
    
    try {
      const url = `${apiBaseUrl}/agentNumbers`;
      
      const response = await fetch(url, {
        headers: {
          'api-key': currentApiKey,
          'restaurant-id': restaurantId
        }
      });
      
      const data = await response.json();
      
      // Handle both array response and object with availableNumbers property
      if (Array.isArray(data)) {
        setAvailableNumbers(data);
      } else if (data.availableNumbers && Array.isArray(data.availableNumbers)) {
        setAvailableNumbers(data.availableNumbers);
      }
    } catch (error) {
      console.error('Failed to fetch available numbers:', error);
      setAvailableNumbers([]);
    } finally {
      setLoadingNumbers(false);
    }
  }, [restaurantId, apiBaseUrl, currentApiKey]);

  // Fetch voice config to check if number is already assigned
  const fetchVoiceConfig = useCallback(async () => {
    if (!restaurantId || !currentApiKey) {
      return;
    }
    
    try {
      const response = await fetch(
        `${apiBaseUrl}/voiceConfig`,
        {
          method: 'GET',
          headers: {
            'api-key': currentApiKey,
            'restaurant-id': restaurantId,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        const config = Array.isArray(data) ? data[0] : (data.data?.[0] || data[0]);
        
        if (config && config.agentNumber) {
          // Number is already assigned
          setAssignedNumber(config.agentNumber);
          setPhoneNumber(config.agentNumber);
          setIsNumberAssigned(true);
        } else {
          // No number assigned
          setAssignedNumber('');
          setIsNumberAssigned(false);
        }
      }
    } catch (error) {
      console.error('Error fetching voice config:', error);
      setIsNumberAssigned(false);
    }
  }, [restaurantId, apiBaseUrl, currentApiKey]);

  // Fetch available numbers and voice config ONLY when modal opens
  // Does NOT trigger on restaurant change to prevent unwanted calls
  useEffect(() => {
    if (isOpen && restaurantId && currentApiKey) {
      fetchVoiceConfig();
      fetchAvailableNumbers();
    }
  }, [isOpen, fetchVoiceConfig, fetchAvailableNumbers]);

  const getMethodBadgeStyles = (method: string) => {
    const baseStyles = 'inline-flex items-center px-3 py-1 rounded text-sm font-semibold uppercase tracking-wide';
    
    switch (method.toUpperCase()) {
      case 'POST':
        return `${baseStyles} bg-blue-100 text-blue-800`;
      default:
        return `${baseStyles} bg-gray-100 text-gray-800`;
    }
  };

  const generateCurlCommand = () => {
    const headers = [
      `'api-key: ${currentApiKey || '<api-key>'}'`,
      `'restaurant-id: ${restaurantId || '<restaurant-id>'}'`
    ];
    
    // Only include body for POST requests
    if (endpoint.method === 'POST') {
      const body = {
        agentNumber: phoneNumber || '8446705375'
      };
      
      return `curl --request ${endpoint.method} \\\n  --url ${apiBaseUrl}${endpoint.endpoint} \\\n  --header ${headers.join(' \\\n  --header ')} \\\n  --header 'Content-Type: application/json' \\\n  --data '${JSON.stringify(body, null, 2)}'`;
    }
    
    // GET request - no body
    return `curl --request ${endpoint.method} \\\n  --url ${apiBaseUrl}${endpoint.endpoint} \\\n  --header ${headers.join(' \\\n  --header ')}`;
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const handleTest = async () => {
    setIsLoading(true);
    setResponse('');

    try {
      const headers: Record<string, string> = {
        'api-key': currentApiKey || '',
        'restaurant-id': restaurantId,
      };

      const fetchOptions: RequestInit = {
        method: endpoint.method,
        headers,
      };

      // Only add body for POST requests, not GET
      if (endpoint.method === 'POST') {
        headers['Content-Type'] = 'application/json';
        fetchOptions.body = JSON.stringify({
          agentNumber: phoneNumber
        });
      }

      const fetchResponse = await fetch(
        `${apiBaseUrl}${endpoint.endpoint}`,
        fetchOptions
      );

      const responseData = await fetchResponse.json();
      setResponse(JSON.stringify(responseData, null, 2));
    } catch (error) {
      setResponse(`Error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 backdrop-blur-sm bg-white/30 flex items-center justify-center p-4">
      <div className="bg-[var(--bg-primary)] rounded-lg border border-[var(--border-color)] max-w-7xl w-full min-w-[1200px] min-h-[600px] max-h-[95vh] overflow-hidden flex flex-col">
        {/* Modal Header */}
        <div className="flex items-center justify-between p-4 border-b border-[var(--border-color)] bg-[var(--bg-secondary)]">
          <div className="flex items-center space-x-3">
            <h3 className="text-base font-semibold text-[var(--text-primary)]">
              {endpoint.title}
            </h3>
          </div>
          
          {/* Right Side - Method, Endpoint and Send Button */}
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-3">
              <span className={`text-xs px-2 py-1 rounded font-semibold ${
                environment === 'sandbox' 
                  ? 'bg-amber-100 text-amber-800' 
                  : 'bg-green-100 text-green-800'
              }`}>
                {environment === 'sandbox' ? 'Sandbox' : 'Production'}
              </span>
              <span className={getMethodBadgeStyles(endpoint.method)}>
                {endpoint.method}
              </span>
              <code className="text-[var(--text-primary)] font-mono text-sm">
                {endpoint.endpoint}
              </code>
              <button
                onClick={() => copyToClipboard(`${apiBaseUrl}${endpoint.endpoint}`)}
                className="text-[var(--text-secondary)] hover:text-[var(--accent-color)] transition-colors p-1"
                title="Copy URL"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              </button>
            </div>
            
            <button
              onClick={handleTest}
              disabled={isLoading || !restaurantId || (endpoint.method === 'POST' && (!phoneNumber || isNumberAssigned))}
              className="px-4 py-2 bg-[var(--accent-color)] hover:bg-[var(--accent-color)]/90 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              <span>{isLoading ? 'Sending...' : 'Send'}</span>
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M2,21L23,12L2,3V10L17,12L2,14V21Z" />
              </svg>
            </button>
            
            <button 
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
        
        <div className="flex flex-1 overflow-hidden min-w-full min-h-[500px]">
          {/* Left Side - Configuration Form */}
          <div className="flex-1 min-w-[600px] min-h-full p-6 overflow-y-auto border-r border-[var(--border-color)]">
            <div className="space-y-6">
              <p className="text-[var(--text-secondary)]">
                Assign a phone number to your restaurant for voice call handling
              </p>

              {/* Authorization Section */}
              <div className="border border-[var(--border-color)] rounded-lg">
                <button
                  onClick={() => setAuthExpanded(!authExpanded)}
                  className="w-full flex items-center justify-between p-4 text-left hover:bg-[var(--bg-hover)] transition-colors"
                >
                  <h4 className="text-base font-semibold text-[var(--text-primary)]">Authorization</h4>
                  <svg 
                    className={`w-4 h-4 transition-transform ${authExpanded ? 'rotate-180' : ''}`}
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                
                {authExpanded && (
                  <div className="px-4 pb-4 border-t border-[var(--border-color)]">
                    <div className="mt-4 space-y-4">
                      {/* API Key Display */}
                      <div>
                        <div className="flex items-center space-x-2 mb-2">
                          <h5 className="text-sm font-medium text-[var(--accent-color)]">API-Key</h5>
                          <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs">string</span>
                          <span className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs font-semibold">required</span>
                        </div>
                        <p className="text-[var(--text-secondary)] text-sm mb-3">API key</p>
                        <div className="w-full px-3 py-2 text-sm bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded text-[var(--text-primary)] font-mono">
                          {currentApiKey || 'Not available - please log in'}
                        </div>
                      </div>

                      {/* Restaurant Selection */}
                      <div>
                        <div className="flex items-center space-x-2 mb-2">
                          <h5 className="text-sm font-medium text-[var(--accent-color)]">restaurant</h5>
                          <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs">select</span>
                          <span className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs font-semibold">required</span>
                        </div>
                        <p className="text-[var(--text-secondary)] text-sm mb-3">Select restaurant from your account</p>
                        {restaurants.length > 0 ? (
                          <select
                            value={selectedRestaurantName}
                            onChange={(e) => {
                              const selectedName = e.target.value;
                              setSelectedRestaurantName(selectedName);
                              const restaurant = restaurants.find(r => r.name === selectedName);
                              if (restaurant) {
                                setRestaurantId(restaurant.restaurantId);
                                dispatch(setSelectedRestaurantId(restaurant.restaurantId));
                              }
                            }}
                            className="w-full px-3 py-2 text-sm bg-[var(--bg-primary)] border border-[var(--border-color)] rounded text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--accent-color)] focus:border-transparent"
                          >
                            <option value="">Select a restaurant</option>
                            {restaurants.map((restaurant) => (
                              <option key={restaurant.restaurantId} value={restaurant.name}>
                                {restaurant.name}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <div className="text-[var(--text-secondary)] text-sm p-3 bg-[var(--bg-secondary)] rounded border border-[var(--border-color)]">
                            No restaurants available. Please register a restaurant first.
                          </div>
                        )}
                        {restaurantId && (
                          <p className="text-[var(--text-muted)] text-xs mt-2">
                            ID: {restaurantId}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Body Parameters - Only show for POST */}
              {endpoint.method === 'POST' && (
                <div className="space-y-6">
                  <h4 className="text-base font-semibold text-[var(--text-primary)]">Body Parameters</h4>
                  
                  {/* Phone Number */}
                  <div>
                    <div className="flex items-center space-x-2 mb-2">
                      <h5 className="text-sm font-medium text-[var(--accent-color)]">agentNumber</h5>
                      <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs">select</span>
                      <span className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs font-semibold">required</span>
                    </div>
                    <p className="text-[var(--text-secondary)] text-sm mb-3">
                      Select an available phone number to assign to the restaurant
                    </p>
                    {loadingNumbers ? (
                      <div className="flex items-center space-x-2 text-[var(--text-secondary)] text-sm p-3 bg-[var(--bg-secondary)] rounded border border-[var(--border-color)]">
                        <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span>Loading available numbers...</span>
                      </div>
                    ) : availableNumbers.length > 0 || isNumberAssigned ? (
                      <>
                        <Dropdown
                          label=""
                          value={phoneNumber}
                          onChange={setPhoneNumber}
                          options={isNumberAssigned 
                            ? [{ value: assignedNumber, label: assignedNumber }]
                            : availableNumbers.map(num => ({ value: num, label: num }))
                          }
                          placeholder="Select an available number"
                          disabled={isNumberAssigned}
                        />
                        {isNumberAssigned && (
                          <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start space-x-2">
                            <svg className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                            </svg>
                            <p className="text-sm text-amber-800">This restaurant already has a phone number assigned.</p>
                          </div>
                        )}
                      </>
                    ) : restaurantId ? (
                      <div className="text-[var(--text-secondary)] text-sm p-3 bg-[var(--bg-secondary)] rounded border border-[var(--border-color)]">
                        No available numbers found. Please contact support.
                      </div>
                    ) : (
                      <div className="text-[var(--text-secondary)] text-sm p-3 bg-[var(--bg-secondary)] rounded border border-[var(--border-color)]">
                        Please select a restaurant first to load available numbers.
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right Side - cURL and Response */}
          <div className="w-96 min-w-[384px] min-h-full flex-shrink-0 flex flex-col">
            {/* Top Half - cURL */}
            <div className="flex-1 min-h-0 max-h-[500px] bg-[var(--bg-secondary)] p-4 border-b border-[var(--border-color)]">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-semibold text-[var(--text-primary)]">cURL</h4>
                <button
                  onClick={() => copyToClipboard(generateCurlCommand())}
                  className="text-[var(--text-secondary)] hover:text-[var(--accent-color)] transition-colors p-1"
                  title="Copy"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                </button>
              </div>
              <div className="bg-[var(--bg-tertiary)] rounded-lg border border-[var(--border-color)] p-4 h-full overflow-auto">
                <pre className="text-sm text-[var(--text-primary)] font-mono whitespace-pre-wrap">
                  <code>{generateCurlCommand()}</code>
                </pre>
              </div>
            </div>

            {/* Bottom Half - Response */}
            <div className="flex-1 min-h-0 max-h-[400px] bg-[var(--bg-secondary)] p-4 flex flex-col">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-semibold text-[var(--text-primary)]">
                  {isLoading ? 'Loading Response...' : response ? '200' : 'Response'}
                </h4>
                {response && !isLoading && (
                  <button
                    onClick={() => copyToClipboard(response)}
                    className="text-[var(--text-secondary)] hover:text-[var(--accent-color)] transition-colors p-1"
                    title="Copy response"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  </button>
                )}
              </div>
              <div className="flex-1 bg-[var(--bg-tertiary)] rounded-lg border border-[var(--border-color)] p-4 overflow-y-auto">
                {isLoading ? (
                  <div className="flex items-center justify-center h-full">
                    <div className="flex items-center space-x-2 text-[var(--text-secondary)]">
                      <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span className="text-sm">Loading response...</span>
                    </div>
                  </div>
                ) : response ? (
                  <pre className="text-sm text-[var(--text-primary)] font-mono whitespace-pre-wrap break-words">
                    <code>{response}</code>
                  </pre>
                ) : (
                  <div className="flex items-center justify-center h-full">
                    <p className="text-[var(--text-muted)] text-sm">
                      Click Send to test the API endpoint
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
